import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, switchMap, take } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { PersistentStorageService } from '../../persistent-storage/persistent-storage.service';
import {
  AcquireLockAndThenUpdateNextCacheResetTimestampDB,
  InitStoreActionTypes,
  InitStoreNoAction,
  UpdateNextCacheResetTimestampDBAndReleaseLock,
} from './init-db.actions';
import { InitStoreFacade } from './init-db.facade';

@Injectable({
  providedIn: 'root',
})
export class InitStoreEffects {
  constructor(
    private action$: Actions,
    private persistentStorageService: PersistentStorageService,
    private initStoreFacade: InitStoreFacade,
  ) {}

  AcquireLockAndThenUpdateNextCacheResetTimestampDB$ = createEffect(() => {
    return this.action$.pipe(
      ofType<AcquireLockAndThenUpdateNextCacheResetTimestampDB>(
        InitStoreActionTypes.AcquireLockAndThenUpdateNextCacheResetTimestampDB,
      ),
      switchMap((action) => {
        return this.initStoreFacade.acquireLock(action.lockFor).pipe(
          take(1),
          switchMap((lockRes) => {
            if (lockRes.status === 'SUCCESS') {
              const currentTime = new Date().getTime();
              return this.initStoreFacade.getConfigurations().pipe(
                take(1),
                switchMap(({ cacheResetTimestampInMilliSeconds }) => {
                  return from(this.persistentStorageService.get('[APP][INPUT_APP_NEXT_CACHE_RESET_TIMESTAMP_DB]')).pipe(
                    take(1),
                    switchMap((res) => {
                      if (res && res.nextCacheResetTimestamp > currentTime)
                        return of(
                          new UpdateNextCacheResetTimestampDBAndReleaseLock(lockRes.lockedBy, {
                            nextCacheResetTimestamp: res.nextCacheResetTimestamp,
                          }),
                        );
                      else {
                        const todayCacheResetTimestamp =
                          new Date(currentTime).setHours(0, 0, 0, 0) + cacheResetTimestampInMilliSeconds;
                        const nextCacheResetTimestamp =
                          currentTime < todayCacheResetTimestamp
                            ? todayCacheResetTimestamp
                            : todayCacheResetTimestamp + 86400000;
                        return from(
                          this.persistentStorageService.set('[APP][INPUT_APP_NEXT_CACHE_RESET_TIMESTAMP_DB]', {
                            nextCacheResetTimestamp,
                          }),
                        ).pipe(
                          take(1),
                          map(
                            () =>
                              new UpdateNextCacheResetTimestampDBAndReleaseLock(lockRes.lockedBy, {
                                nextCacheResetTimestamp,
                              }),
                          ),
                        );
                      }
                    }),
                  );
                }),
              );
            } else {
              return of(new InitStoreNoAction());
            }
          }),
        );
      }),
    );
  });

  UpdateNextCacheResetTimestampDBAndReleaseLock$ = createEffect(() => {
    return this.action$.pipe(
      ofType<UpdateNextCacheResetTimestampDBAndReleaseLock>(
        InitStoreActionTypes.UpdateNextCacheResetTimestampDBAndReleaseLock,
      ),
      map((action) => {
        this.initStoreFacade.initFinished(action.lockedBy);
        return new InitStoreNoAction();
      }),
    );
  });
}
